// src/components/ReportConfirmationModal.js
import React, { useState, useEffect } from 'react';
import { timeEntriesAPI, materialUsagesAPI } from '../services/api';

const ReportConfirmationModal = ({ isOpen, onClose, onConfirm, projectId }) => {
  const [timeEntries, setTimeEntries] = useState([]);
  const [materialUsages, setMaterialUsages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingTime, setEditingTime] = useState(null);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [timeFormData, setTimeFormData] = useState({});
  const [materialFormData, setMaterialFormData] = useState({});

  useEffect(() => {
    if (isOpen && projectId) {
      fetchData();
    }
  }, [isOpen, projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Zeitaufwände abrufen - angepasster Pfad
      const timeResponse = await timeEntriesAPI.getByProject(projectId);
      setTimeEntries(timeResponse.data.timeEntries || []);

      // Materialaufwände abrufen mit korrektem Pfad
      const materialResponse = await materialUsagesAPI.getByProject(projectId);
      setMaterialUsages(materialResponse.data.materialUsages || []);

      setError('');
    } catch (err) {
      console.error('Fehler beim Laden der Daten:', err);
      setError('Fehler beim Laden der Einträge: ' + (err.response?.data?.error || err.message));
      
      // Fallback: Zeige leere Arrays, wenn die Daten nicht geladen werden können
      setTimeEntries([]);
      setMaterialUsages([]);
    } finally {
      setLoading(false);
    }
  };

  // Zeiterfassung bearbeiten
  const startEditingTime = (timeEntry) => {
    setEditingTime(timeEntry.id);
    setTimeFormData({
      start_time: formatDateTimeForInput(timeEntry.start_time),
      end_time: formatDateTimeForInput(timeEntry.end_time),
      travel_allowance: timeEntry.travel_allowance,
      km: timeEntry.km || 0
    });
  };

  const saveTimeEdit = async () => {
    try {
      // Formatierte Daten ohne Z-Suffix, um Zeitzonenprobleme zu vermeiden
      const formattedData = {
        ...timeFormData,
        start_time: timeFormData.start_time ? timeFormData.start_time.replace('Z', '') : null,
        end_time: timeFormData.end_time ? timeFormData.end_time.replace('Z', '') : null
      };
      
      await timeEntriesAPI.update(editingTime, formattedData);
      setEditingTime(null);
      fetchData(); // Daten neu laden
      setError('');
    } catch (err) {
      console.error('Fehler beim Speichern der Zeiterfassung:', err);
      setError('Fehler beim Speichern: ' + (err.response?.data?.error || err.message));
    }
  };

  // Materialverbrauch bearbeiten
  const startEditingMaterial = (material) => {
    setEditingMaterial(material.id);
    setMaterialFormData({
      quantity: material.quantity,
      material_id: material.material_id
    });
  };

  const saveMaterialEdit = async () => {
    try {
      await materialUsagesAPI.update(editingMaterial, materialFormData);
      setEditingMaterial(null);
      fetchData(); // Daten neu laden
      setError('');
    } catch (err) {
      console.error('Fehler beim Speichern des Materials:', err);
      setError('Fehler beim Speichern: ' + (err.response?.data?.error || err.message));
    }
  };

  // Zeiterfassung löschen
  const deleteTimeEntry = async (timeId) => {
    if (!window.confirm('Möchten Sie diesen Zeiteintrag wirklich löschen?')) return;
    
    try {
      await timeEntriesAPI.delete(timeId);
      fetchData(); // Daten neu laden
      setError('');
    } catch (err) {
      console.error('Fehler beim Löschen der Zeiterfassung:', err);
      setError('Fehler beim Löschen: ' + (err.response?.data?.error || err.message));
    }
  };

  // Materialverbrauch löschen
  const deleteMaterialUsage = async (materialId) => {
    if (!window.confirm('Möchten Sie diesen Materialeintrag wirklich löschen?')) return;
    
    try {
      await materialUsagesAPI.delete(materialId);
      fetchData(); // Daten neu laden
      setError('');
    } catch (err) {
      console.error('Fehler beim Löschen des Materials:', err);
      setError('Fehler beim Löschen: ' + (err.response?.data?.error || err.message));
    }
  };

  // Hilfsfunktion für Datum-Formatierung
  const formatDateTimeForInput = (dateString) => {
    if (!dateString) return '';
    
    // Stellen Sie sicher, dass das 'Z' entfernt wird, um eine Zeitzonenverschiebung zu vermeiden
    if (dateString.indexOf('Z') !== -1) {
      dateString = dateString.replace('Z', '');
    }
    
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16); // Format: "YYYY-MM-DDTHH:MM"
  };

  // Hilfsfunktion für Datumsanzeige
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    
    // Stellen Sie sicher, dass das 'Z' entfernt wird, um eine Zeitzonenverschiebung zu vermeiden
    if (dateString.indexOf('Z') !== -1) {
      dateString = dateString.replace('Z', '');
    }
    
    const date = new Date(dateString);
    return date.toLocaleString('de-DE');
  };

  // Hilfsfunktion für Stundenberechnung
  const calculateHours = (startTime, endTime) => {
    if (!startTime || !endTime) return '-';
    
    // Verwende konsistente Formatierung für beide Zeitwerte
    let start, end;
    
    if (startTime.indexOf('Z') !== -1) {
      start = new Date(startTime.replace('Z', ''));
    } else {
      start = new Date(startTime);
    }
    
    if (endTime.indexOf('Z') !== -1) {
      end = new Date(endTime.replace('Z', ''));
    } else {
      end = new Date(endTime);
    }
    
    // Berechne Stundendifferenz direkt
    return ((end - start) / (1000 * 60 * 60)).toFixed(2);
  };

  // Eingabefeld-Änderungen für Zeiterfassung
  const handleTimeFormChange = (e) => {
    setTimeFormData({
      ...timeFormData,
      [e.target.name]: e.target.name === 'travel_allowance' 
        ? e.target.checked 
        : e.target.value
    });
  };

  // Eingabefeld-Änderungen für Material
  const handleMaterialFormChange = (e) => {
    setMaterialFormData({
      ...materialFormData,
      [e.target.name]: e.target.value
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Rapport bestätigen</h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              &times;
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="text-center py-10">Lade Daten...</div>
          ) : (
            <>
              {/* Zeiterfassung */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4">Zeiterfassung</h3>
                {timeEntries.length === 0 ? (
                  <p className="text-gray-500">Keine Zeiterfassungen vorhanden</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mitarbeiter</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ende</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stunden</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reisezeit</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">KM</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktionen</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {timeEntries.map((entry) => (
                          <tr key={entry.id}>
                            {editingTime === entry.id ? (
                              // Bearbeitungsmodus
                              <>
                                <td className="px-6 py-4">{`${entry.first_name} ${entry.last_name}`}</td>
                                <td className="px-6 py-4">
                                  <input
                                    type="datetime-local"
                                    name="start_time"
                                    value={timeFormData.start_time}
                                    onChange={handleTimeFormChange}
                                    className="border rounded px-2 py-1 w-full"
                                  />
                                </td>
                                <td className="px-6 py-4">
                                  <input
                                    type="datetime-local"
                                    name="end_time"
                                    value={timeFormData.end_time}
                                    onChange={handleTimeFormChange}
                                    className="border rounded px-2 py-1 w-full"
                                  />
                                </td>
                                <td className="px-6 py-4">
                                  {calculateHours(timeFormData.start_time, timeFormData.end_time)}
                                </td>
                                <td className="px-6 py-4">
                                  <input
                                    type="checkbox"
                                    name="travel_allowance"
                                    checked={timeFormData.travel_allowance}
                                    onChange={handleTimeFormChange}
                                    className="h-4 w-4 text-primary-600"
                                  />
                                </td>
                                <td className="px-6 py-4">
                                  <input
                                    type="number"
                                    name="km"
                                    value={timeFormData.km}
                                    onChange={handleTimeFormChange}
                                    className="border rounded px-2 py-1 w-full"
                                  />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  <button
                                    onClick={saveTimeEdit}
                                    className="text-primary-600 hover:text-primary-900 mr-3"
                                  >
                                    Speichern
                                  </button>
                                  <button
                                    onClick={() => setEditingTime(null)}
                                    className="text-gray-500 hover:text-gray-700"
                                  >
                                    Abbrechen
                                  </button>
                                </td>
                              </>
                            ) : (
                              // Anzeigemodus
                              <>
                                <td className="px-6 py-4">{`${entry.first_name} ${entry.last_name}`}</td>
                                <td className="px-6 py-4">{formatDateTime(entry.start_time)}</td>
                                <td className="px-6 py-4">{formatDateTime(entry.end_time)}</td>
                                <td className="px-6 py-4">{calculateHours(entry.start_time, entry.end_time)}</td>
                                <td className="px-6 py-4">{entry.travel_allowance ? 'Ja' : 'Nein'}</td>
                                <td className="px-6 py-4">{entry.km || '-'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  <button
                                    onClick={() => startEditingTime(entry)}
                                    className="text-primary-600 hover:text-primary-900 mr-3"
                                  >
                                    Bearbeiten
                                  </button>
                                  <button
                                    onClick={() => deleteTimeEntry(entry.id)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    Löschen
                                  </button>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Materialverbrauch */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4">Materialverbrauch</h3>
                {materialUsages.length === 0 ? (
                  <p className="text-gray-500">Kein Material erfasst</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Material</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Menge</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Einheit</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktionen</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {materialUsages.map((material) => (
                          <tr key={material.id}>
                            {editingMaterial === material.id ? (
                              // Bearbeitungsmodus
                              <>
                                <td className="px-6 py-4">{material.material_name}</td>
                                <td className="px-6 py-4">
                                  <input
                                    type="number"
                                    name="quantity"
                                    value={materialFormData.quantity}
                                    onChange={handleMaterialFormChange}
                                    className="border rounded px-2 py-1 w-32"
                                    min="0"
                                    step="0.01"
                                  />
                                </td>
                                <td className="px-6 py-4">{material.unit}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  <button
                                    onClick={saveMaterialEdit}
                                    className="text-primary-600 hover:text-primary-900 mr-3"
                                  >
                                    Speichern
                                  </button>
                                  <button
                                    onClick={() => setEditingMaterial(null)}
                                    className="text-gray-500 hover:text-gray-700"
                                  >
                                    Abbrechen
                                  </button>
                                </td>
                              </>
                            ) : (
                              // Anzeigemodus
                              <>
                                <td className="px-6 py-4">{material.material_name}</td>
                                <td className="px-6 py-4">{material.quantity}</td>
                                <td className="px-6 py-4">{material.unit}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  <button
                                    onClick={() => startEditingMaterial(material)}
                                    className="text-primary-600 hover:text-primary-900 mr-3"
                                  >
                                    Bearbeiten
                                  </button>
                                  <button
                                    onClick={() => deleteMaterialUsage(material.id)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    Löschen
                                  </button>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Bestätigungsbuttons */}
              <div className="flex justify-end mt-6 space-x-4">
                <button
                  onClick={onClose}
                  className="bg-gray-300 hover:bg-gray-400 text-black px-4 py-2 rounded"
                >
                  Abbrechen
                </button>
                <button
                  onClick={onConfirm}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded"
                >
                  Einträge bestätigen und Rapport erstellen
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportConfirmationModal;